import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BEResponse, BEStatus } from '@qtek/core/api-core';

import {
  UserRole,
  EntityMeta,
  CompanyInvestorVisibility,
  FeatureStage,
  HTTP_BASE_URL,
  OrganizationDetails,
  OrganizationLicenseData,
  OrganizationLicenseUpdatePayload,
} from '@qtek/shared/models';
import { map, Observable } from 'rxjs';

export interface OrganizationsGridResponse {
  sts?: BEStatus & { meta?: EntityMeta };
  res?: OrganizationDetails[];
}

export interface OrganizationUserUpdatePayload {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  status?: number;
  role?: UserRole;
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationsService {
  protected http = inject(HttpClient);
  private httpBaseUrl = inject(HTTP_BASE_URL);

  private readonly companyUrl = `${this.httpBaseUrl}/api/v2/model/Company`;
  private readonly adminUrl = `${this.httpBaseUrl}/api/v2/admin`;

  getOrganizations(): Observable<OrganizationsGridResponse> {
    return this.http.get<OrganizationsGridResponse>(this.companyUrl, {
      params: { view: 'admin' },
      withCredentials: true,
    });
  }

  getOrganizationDetails(
    id: string
  ): Observable<BEResponse<OrganizationDetails>> {
    return this.http.get<BEResponse<OrganizationDetails>>(
      `${this.companyUrl}/${id}`,
      { params: { view: 'admin' }, withCredentials: true }
    );
  }

  getOrganizationLicenses(): Observable<BEResponse<OrganizationLicenseData[]>> {
    return this.http.get<BEResponse<OrganizationLicenseData[]>>(
      `${this.httpBaseUrl}/api/v2/model/LicenseTemplate`,
      { withCredentials: true }
    );
  }

  deleteOrganizationAccount(acnId: string): Observable<BEResponse<unknown>> {
    return this.http.delete<BEResponse<unknown>>(
      `${this.companyUrl}/${acnId}`,
      { withCredentials: true }
    );
  }

  updateOrganization(
    id: string,
    testOrganizationFlag: boolean
  ): Observable<BEResponse<OrganizationDetails>> {
    return this.http.put<BEResponse<OrganizationDetails>>(
      `${this.companyUrl}/${id}`,
      { testOrganizationFlag },
      { withCredentials: true }
    );
  }

  updateOrganizationLicense(
    payload: OrganizationLicenseUpdatePayload
  ): Observable<BEResponse<unknown>> {
    return this.http.post<BEResponse<unknown>>(
      `${this.httpBaseUrl}/api/v2/model/LicenseInstance`,
      payload,
      { withCredentials: true }
    );
  }

  updateOrganizationUser(
    userId: string,
    payload: OrganizationUserUpdatePayload
  ): Observable<BEResponse<string>> {
    return this.http.put<BEResponse<string>>(
      `${this.adminUrl}/update-user/${userId}`,
      payload,
      { withCredentials: true }
    );
  }

  resetUserPassword(userId: string): Observable<BEResponse<string>> {
    return this.http.post<BEResponse<string>>(
      `${this.adminUrl}/reset-password-email/${userId}`,
      {},
      { withCredentials: true }
    );
  }

  resetUserPasswordLink(userId: string): Observable<BEResponse<string>> {
    return this.http.post<BEResponse<string>>(
      `${this.adminUrl}/reset-password-link/${userId}`,
      {},
      { withCredentials: true }
    );
  }

  generateOnBehalfUrl(userId: string): Observable<string> {
    return this.http
      .get<
        BEResponse<string>
      >(`${this.adminUrl}/generate-on-behalf-token/${userId}`, { withCredentials: true })
      .pipe(map(r => r.res));
  }

  loginAsAnotherUserByToken(token: string) {
    const headers = new HttpHeaders().append(
      'X-Skip-Revalidate-Error-Handling',
      ''
    );
    return this.http.post(
      `${this.adminUrl}/login-as-another-user/${encodeURIComponent(token)}`,
      {},
      { withCredentials: true, headers }
    );
  }

  updateOrganizationName(
    acnId: string,
    name: string
  ): Observable<BEResponse<string>> {
    return this.http.put<BEResponse<string>>(
      `${this.adminUrl}/update-organization/${acnId}`,
      { name },
      { withCredentials: true }
    );
  }

  updateInvestorVisibility(
    acnId: string,
    investorVisibility: CompanyInvestorVisibility
  ): Observable<BEResponse<string>> {
    return this.http.put<BEResponse<string>>(
      `${this.adminUrl}/update-organization/${acnId}`,
      { investorVisibility },
      { withCredentials: true }
    );
  }

  updateFeatureStage(
    acnId: string,
    featureStage: FeatureStage
  ): Observable<BEResponse<string>> {
    return this.http.put<BEResponse<string>>(
      `${this.adminUrl}/update-organization/${acnId}`,
      { featureStage },
      { withCredentials: true }
    );
  }

  addOrganizationDomain(
    acnId: string,
    domain: string
  ): Observable<BEResponse<string>> {
    return this.http.post<BEResponse<string>>(
      `${this.adminUrl}/add-domain/${acnId}`,
      { domain },
      { withCredentials: true }
    );
  }

  removeLoginBlock(email: string): Observable<BEResponse<string>> {
    return this.http.post<BEResponse<string>>(
      `${this.adminUrl}/remove-login-block/${encodeURIComponent(email)}`,
      {},
      { withCredentials: true }
    );
  }
}
